import type { ContractField } from "@/api/client";

/**
 * Input dinâmico baseado no `ContractField.kind`. Cada tipo renderiza
 * o widget adequado (address picker, slider, toggle, etc) e devolve o
 * valor já no formato esperado pelo encoding (address checksumado,
 * uint stringified, etc).
 *
 * O valor sempre vive como string (UX) e a serialização final fica a
 * cargo do componente pai antes de mandar pra API.
 */
interface Props {
  field: ContractField;
  value: string;
  onChange: (v: string) => void;
  resolveDefault: (token: string) => string;
}

export function FieldInput({ field, value, onChange, resolveDefault }: Props) {
  const placeholder = field.defaultValue
    ? field.defaultValue.startsWith("@")
      ? resolveDefault(field.defaultValue)
      : field.defaultValue
    : undefined;

  if (field.kind === "bool") {
    const checked = value === "true";
    return (
      <button type="button"
              onClick={() => onChange(checked ? "false" : "true")}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                checked ? "bg-green-600" : "bg-gray-300"
              }`}
              aria-checked={checked} role="switch">
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`} />
      </button>
    );
  }

  if (field.kind === "address" || field.kind === "string" || field.kind === "symbol") {
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(field.kind === "symbol" ? e.target.value.toUpperCase() : e.target.value)}
        placeholder={placeholder}
        pattern={field.kind === "address" ? "0x[a-fA-F0-9]{40}" : undefined}
        maxLength={field.kind === "symbol" ? 20 : undefined}
        className={`w-full border rounded px-3 py-2 ${
          field.kind === "address" ? "font-mono text-sm" : ""
        }`}
      />
    );
  }

  if (field.kind === "address-list") {
    const items = value ? value.split(",").map((s) => s.trim()).filter(Boolean) : [];
    return (
      <div className="space-y-2">
        {items.map((addr, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              value={addr}
              onChange={(e) => {
                const copy = [...items];
                copy[i] = e.target.value;
                onChange(copy.join(","));
              }}
              className="flex-1 border rounded px-3 py-2 font-mono text-sm"
              pattern="0x[a-fA-F0-9]{40}"
              placeholder="0x..."
            />
            <button type="button"
                    onClick={() => onChange(items.filter((_, idx) => idx !== i).join(","))}
                    className="text-red-600 px-2">×</button>
          </div>
        ))}
        <button type="button"
                onClick={() => onChange([...items, ""].join(","))}
                className="text-sm text-blue-700 underline">+ adicionar endereço</button>
      </div>
    );
  }

  if (field.kind === "tokens" || field.kind === "uint256") {
    return (
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        min={field.min ?? 0}
        max={field.max}
        className="w-full border rounded px-3 py-2"
      />
    );
  }

  if (field.kind === "duration-months") {
    const months = Number(value || "0");
    const max = field.max ?? 120;
    const min = field.min ?? 0;
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <input
            type="range" min={min} max={max} step={1}
            value={months}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1"
          />
          <div className="w-24">
            <input
              type="number"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              min={min} max={max}
              className="w-full border rounded px-2 py-1 text-right"
            />
          </div>
          <span className="text-sm text-gray-500 w-12">meses</span>
        </div>
        <p className="text-xs text-gray-500">
          ≈ {(months / 12).toFixed(1)} anos
        </p>
      </div>
    );
  }

  if (field.kind === "duration-seconds") {
    return (
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        min={0}
        className="w-full border rounded px-3 py-2"
      />
    );
  }

  if (field.kind === "timestamp") {
    return (
      <div>
        <input
          type="datetime-local"
          value={value && /^\d+$/.test(value)
            ? new Date(Number(value) * 1000).toISOString().slice(0, 16)
            : value}
          onChange={(e) => {
            if (!e.target.value) {
              onChange("");
              return;
            }
            const unix = Math.floor(new Date(e.target.value).getTime() / 1000);
            onChange(String(unix));
          }}
          className="w-full border rounded px-3 py-2"
        />
        <button type="button"
                onClick={() => onChange(String(Math.floor(Date.now() / 1000)))}
                className="text-xs text-blue-700 underline mt-1">
          usar agora
        </button>
      </div>
    );
  }

  return (
    <input
      type="text" value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded px-3 py-2" placeholder={placeholder}
    />
  );
}
